import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Paperclip, X, ArrowUpRight, Check } from "lucide-react";
import { Lines, FadeUp } from "../components/site/Reveal";
import { SectionLabel } from "../components/site/Btn";
import { useSeo } from "../components/site/Seo";
import { api, imgSrc, CONTACT_EMAIL } from "../lib/api";
import { useLang, stoneName } from "../i18n/lang";
import { useStone } from "../lib/queries";

const TYPES = ["dining_table", "coffee_table", "side_table", "console", "sculptural_object", "bespoke_furniture", "other"];
const INIT = { name: "", email: "", country: "", project_type: "", stone_code: "", dimensions: "", intended_use: "", description: "", budget: "", timeline: "", consent: false, website: "" };

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="eyebrow !text-[0.66rem] text-[#555555]">{label}{required && " *"}</span>
    {children}
  </label>
);

export default function Contact() {
  const { t, lang, to } = useLang();
  useSeo(...t.seo.contact);
  const c = t.contact;
  const [params] = useSearchParams();
  const pre = params.get("stone") || "";
  const { data: preStone } = useStone(pre);
  const [v, setV] = useState({ ...INIT, stone_code: pre.toUpperCase() });
  const [files, setFiles] = useState([]);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const linked = preStone && preStone.code === v.stone_code && preStone.availability !== "sold" ? preStone : null;

  useEffect(() => { if (pre) setV((p) => ({ ...p, stone_code: pre.toUpperCase() })); }, [pre]);

  const addFiles = async (list) => {
    setError("");
    for (const file of Array.from(list).slice(0, 6 - files.length)) {
      const tmp = { name: file.name, status: "uploading", path: "" };
      setFiles((f) => [...f, tmp]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/enquiry", fd);
        setFiles((f) => f.map((x) => (x === tmp ? { ...x, status: "done", path: data.path } : x)));
      } catch {
        setFiles((f) => f.filter((x) => x !== tmp));
        setError(c.fileError);
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!v.name || !v.email || !v.country || !v.project_type || v.description.trim().length < 10 || !v.consent) {
      setError(c.required);
      return;
    }
    setState("sending");
    try {
      await api.post("/enquiries", { ...v, lang, attachments: files.filter((f) => f.path).map((f) => f.path) });
      setState("done");
      window.__lenis?.scrollTo(0);
    } catch {
      setState("idle");
      setError(c.error);
    }
  };

  if (state === "done") return (
    <main className="min-h-[90vh] px-6 sm:px-10 lg:px-14 pt-44 pb-32" data-testid="contact-success">
      <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.8 }} className="w-14 h-14 border border-[#1A1A1A] flex items-center justify-center"><Check strokeWidth={1.25} /></motion.div>
      <Lines as="h1" onMount lines={[c.successTitle]} className="f-serif font-light text-4xl sm:text-6xl tracking-[-0.02em] leading-[1.05] mt-10 max-w-4xl" />
      <p className="mt-8 text-lg text-[#3A3A3A] max-w-xl leading-relaxed">{c.successText}</p>
      <button type="button" className="mt-12 eyebrow link-line" data-testid="contact-send-another" onClick={() => { setV(INIT); setFiles([]); setState("idle"); }}>{c.another}</button>
    </main>
  );

  return (
    <main data-testid="contact-page">
      <section className="px-6 sm:px-10 lg:px-14 pt-40 sm:pt-48 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <SectionLabel n={0}>{c.label}</SectionLabel>
            <Lines as="h1" onMount lines={[c.title]} className="f-serif font-light tracking-[-0.025em] leading-[0.98] text-5xl sm:text-6xl lg:text-7xl mt-10" />
            <FadeUp delay={0.2}><p className="mt-8 text-lg leading-[1.75] text-[#3A3A3A]">{c.text}</p></FadeUp>
            <div className="mt-12 hidden lg:block border-t border-[#1A1A1A]/15 pt-8">
              <p className="eyebrow !text-[0.66rem] text-[#555555]">{c.next}</p>
              <ol className="mt-4 space-y-3 text-sm text-[#3A3A3A]">{c.nextSteps.map((s, i) => <li key={i} className="flex gap-4"><span className="f-mono text-[#555555]">0{i + 1}</span>{s}</li>)}</ol>
              <p className="eyebrow !text-[0.66rem] text-[#555555] mt-10">{c.direct}</p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="mt-3 inline-block link-line" data-testid="contact-direct-email">{CONTACT_EMAIL}</a>
            </div>
          </div>
        </div>

        <form onSubmit={submit} noValidate className="lg:col-span-7 lg:col-start-6 space-y-10" data-testid="enquiry-form">
          {linked && (
            <div className="flex items-center gap-5 bg-[#F0F0F0] p-4" data-testid="prefilled-stone">
              <img src={imgSrc(linked.images?.[0])} alt="" className="w-16 h-20 object-cover" />
              <div className="flex-1 min-w-0">
                <p className="eyebrow !text-[0.62rem] text-[#555555]">{c.selected}</p>
                <p className="f-serif text-xl font-light truncate">{stoneName(linked, lang)}</p>
                <p className="f-mono text-xs mt-1">{linked.code}</p>
              </div>
              <button type="button" onClick={() => setV((p) => ({ ...p, stone_code: "" }))} aria-label={c.remove} data-testid="remove-prefilled-stone" className="p-2"><X className="w-4 h-4" strokeWidth={1.25} /></button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
            <Field label={c.name} required><input className="field" autoComplete="name" value={v.name} onChange={set("name")} data-testid="input-name" /></Field>
            <Field label={c.email} required><input className="field" type="email" autoComplete="email" value={v.email} onChange={set("email")} data-testid="input-email" /></Field>
            <Field label={c.country} required><input className="field" autoComplete="country-name" value={v.country} onChange={set("country")} data-testid="input-country" /></Field>
            <Field label={c.code}><input className="field f-mono uppercase" value={v.stone_code} onChange={set("stone_code")} placeholder="SE-MB-001" data-testid="input-stone-code" /></Field>
          </div>
          <fieldset>
            <legend className="eyebrow !text-[0.66rem] text-[#555555] mb-4">{c.type} *</legend>
            <div className="flex flex-wrap gap-2" role="radiogroup">
              {TYPES.map((k) => (
                <button key={k} type="button" role="radio" aria-checked={v.project_type === k} onClick={() => setV((p) => ({ ...p, project_type: k }))} data-testid={`type-${k}`}
                  className={`px-4 min-h-[44px] text-sm border transition-colors duration-400 ${v.project_type === k ? "bg-[#1A1A1A] text-[#FFFFFF] border-[#1A1A1A]" : "border-[#1A1A1A]/25 hover:border-[#1A1A1A]"}`}>{c.types[k]}</button>
              ))}
            </div>
          </fieldset>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
            <Field label={c.dims}><input className="field" value={v.dimensions} onChange={set("dimensions")} placeholder="240 × 110 cm" data-testid="input-dimensions" /></Field>
            <Field label={c.use}><input className="field" value={v.intended_use} onChange={set("intended_use")} data-testid="input-use" /></Field>
          </div>
          <Field label={c.desc} required><textarea className="field min-h-[140px] resize-y" value={v.description} onChange={set("description")} data-testid="input-description" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
            <Field label={c.budget}><input className="field" value={v.budget} onChange={set("budget")} data-testid="input-budget" /></Field>
            <Field label={c.timeline}><input className="field" value={v.timeline} onChange={set("timeline")} data-testid="input-timeline" /></Field>
          </div>
          <input type="text" tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} className="hidden" aria-hidden="true" />
          <div>
            <p className="eyebrow !text-[0.66rem] text-[#555555]">{c.files}</p>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={files.length >= 6} data-testid="upload-trigger"
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              className="mt-3 w-full border border-dashed border-[#1A1A1A]/30 hover:border-[#1A1A1A] transition-colors duration-500 py-8 flex flex-col items-center gap-2 text-sm">
              <Paperclip className="w-4 h-4" strokeWidth={1.25} /><span>{c.addFiles}</span><span className="text-xs text-[#555555]">{c.filesHint}</span>
            </button>
            <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" data-testid="upload-input"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            {files.length > 0 && (
              <ul className="mt-3 space-y-2" data-testid="upload-list">
                {files.map((f, i) => (
                  <li key={f.name + i} className="flex items-center justify-between text-sm border-b border-[#1A1A1A]/10 py-2">
                    <span className="truncate">{f.name}</span>
                    <span className="flex items-center gap-3 text-xs text-[#555555]">{f.status === "uploading" ? c.uploading : <Check className="w-4 h-4" strokeWidth={1.25} />}
                      <button type="button" aria-label={c.remove} onClick={() => setFiles((x) => x.filter((_, j) => j !== i))}><X className="w-4 h-4" strokeWidth={1.25} /></button></span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <label className="flex items-start gap-4 cursor-pointer text-sm leading-relaxed">
            <input type="checkbox" checked={v.consent} onChange={set("consent")} className="mt-1 w-4 h-4 accent-[#1A1A1A]" data-testid="input-consent" />
            <span>{c.consent} <a href={to("/privacy")} target="_blank" rel="noreferrer" className="underline underline-offset-2">{t.footer.privacy}</a></span>
          </label>
          {error && <p role="alert" className="text-sm text-[#8a3b2e] border-l-2 border-[#8a3b2e] pl-4" data-testid="form-error">{error}</p>}
          <button type="submit" disabled={state === "sending" || files.some((f) => f.status === "uploading")} data-testid="enquiry-submit"
            className="group w-full sm:w-auto inline-flex items-center justify-between gap-10 min-h-[56px] px-8 bg-[#1A1A1A] text-[#FFFFFF] text-[0.8rem] tracking-[0.14em] uppercase hover:bg-[#3A3A3A] transition-colors duration-500 disabled:opacity-50">
            {state === "sending" ? c.sending : c.submit}<ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.25} />
          </button>
          <p className="lg:hidden text-sm">{c.direct}: <a href={`mailto:${CONTACT_EMAIL}`} className="underline underline-offset-2">{CONTACT_EMAIL}</a></p>
        </form>
      </section>
    </main>
  );
}
